"use client";

import { useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link } from "@/i18n/navigation";
import { getPathname } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";

/**
 * « Gardez votre dossier » : une adresse, et un lien de connexion part par
 * e-mail (app/api/tools/login-link). Pas de mot de passe, pas de formulaire
 * d'inscription : le lien connecte au compte de l'adresse, ou le crée.
 *
 * L'ÉCRAN NE DIT JAMAIS SI L'ADRESSE A UN COMPTE. Il affiche la même chose
 * dans les deux cas ; seul l'e-mail le sait.
 */

const T = {
    fr: {
        titre: "Gardez votre dossier",
        texte: "Recevez un lien par e-mail. Il vous connecte sans mot de passe, et votre dossier vous suit sur tous vos appareils. Pas encore de compte ? Le lien le crée.",
        libelle: "Votre e-mail",
        bouton: "Recevoir le lien",
        envoi: "Envoi…",
        envoyeTitre: "Regardez votre boîte mail",
        envoyeTexte: "Un lien vient de partir vers {email}. Il reste valable 30 minutes.",
        pasRecu: "Rien reçu ? Regardez dans les indésirables, ou",
        renvoyer: "changez d'adresse",
        motDePasse: "Se connecter avec un mot de passe",
        erreurEmail: "Cette adresse ne semble pas valide.",
        erreurTrop: "Trois liens sont déjà partis vers cette adresse. Réessayez dans un quart d'heure.",
        erreurRecaptcha: "La vérification anti-robot n'a pas abouti. Réessayez dans un instant.",
        erreur: "L'envoi n'a pas marché. Réessayez dans un instant.",
        fermer: "Fermer",
    },
    en: {
        titre: "Keep your profile",
        texte: "Get a link by email. It signs you in with no password, and your profile follows you on all your devices. No account yet? The link creates one.",
        libelle: "Your email",
        bouton: "Send me the link",
        envoi: "Sending…",
        envoyeTitre: "Check your inbox",
        envoyeTexte: "A link is on its way to {email}. It works for 30 minutes.",
        pasRecu: "Nothing yet? Check your spam folder, or",
        renvoyer: "use another address",
        motDePasse: "Sign in with a password",
        erreurEmail: "This address does not look valid.",
        erreurTrop: "Three links have already gone to this address. Try again in fifteen minutes.",
        erreurRecaptcha: "The anti-robot check did not go through. Please try again in a moment.",
        erreur: "Sending failed. Please try again in a moment.",
        fermer: "Close",
    },
};

const ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ToolsAccountSheet({ lang = "fr", ouvert, onFermer, retour = "/tools/my-situation" }) {
    const t = T[lang] || T.fr;
    return (
        <BottomSheetModal isOpen={ouvert} onClose={onFermer} maxWidthClass="sm:max-w-md" labelledBy="dossier-compte-titre">
            {ouvert && (
                // Le script reCAPTCHA ne se charge qu'à l'ouverture de la feuille.
                <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY} scriptProps={{ async: true, defer: true }}>
                    <Formulaire t={t} lang={lang} retour={retour} />
                </GoogleReCaptchaProvider>
            )}
        </BottomSheetModal>
    );
}

function Formulaire({ t, lang, retour }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [email, setEmail] = useState("");
    const [etat, setEtat] = useState({ statut: "pret" });

    const envoyer = async (evenement) => {
        evenement.preventDefault();
        const adresse = email.trim();
        if (!ADRESSE.test(adresse)) {
            setEtat({ statut: "erreur", message: t.erreurEmail });
            return;
        }
        if (!executeRecaptcha) {
            setEtat({ statut: "erreur", message: t.erreurRecaptcha });
            return;
        }
        setEtat({ statut: "envoi" });
        try {
            const recaptchaToken = await executeRecaptcha("tools_login_link");
            const reponse = await fetch("/api/tools/login-link", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: adresse, locale: lang, recaptchaToken }),
            });
            if (reponse.ok) {
                setEtat({ statut: "envoye", email: adresse });
                return;
            }
            const donnees = await reponse.json().catch(() => ({}));
            const message =
                donnees.error === "EMAIL_INVALIDE"
                    ? t.erreurEmail
                    : donnees.error === "TROP_DE_DEMANDES"
                      ? t.erreurTrop
                      : donnees.error === "RECAPTCHA"
                        ? t.erreurRecaptcha
                        : t.erreur;
            setEtat({ statut: "erreur", message });
        } catch (e) {
            setEtat({ statut: "erreur", message: t.erreur });
        }
    };

    if (etat.statut === "envoye") {
        return (
            <div className="p-6 sm:p-8" role="status">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <SolarIcon name="letter-opened-linear" className="h-6 w-6 text-primary" />
                </div>
                <h2 id="dossier-compte-titre" className="mb-2 text-xl font-bold font-sourceSans dark:text-white">
                    {t.envoyeTitre}
                </h2>
                <p className="mb-4 text-gray-600 dark:text-lightBlue">{t.envoyeTexte.replace("{email}", etat.email)}</p>
                <p className="mb-0 text-sm text-gray-500 dark:text-lightBlue/70">
                    {t.pasRecu}{" "}
                    <button
                        type="button"
                        onClick={() => setEtat({ statut: "pret" })}
                        className="font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary"
                    >
                        {t.renvoyer}
                    </button>
                    .
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={envoyer} noValidate className="p-6 sm:p-8">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10">
                <SolarIcon name="letter-linear" className="h-6 w-6 text-gray-900 dark:text-white" />
            </div>
            <h2 id="dossier-compte-titre" className="mb-2 text-xl font-bold font-sourceSans dark:text-white">
                {t.titre}
            </h2>
            <p className="mb-5 text-gray-600 dark:text-lightBlue">{t.texte}</p>
            <label htmlFor="dossier-compte-email" className="mb-1.5 block text-sm font-semibold dark:text-white">
                {t.libelle}
            </label>
            <input
                id="dossier-compte-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    if (etat.statut === "erreur") setEtat({ statut: "pret" });
                }}
                aria-invalid={etat.statut === "erreur" || undefined}
                aria-describedby={etat.statut === "erreur" ? "dossier-compte-erreur" : undefined}
                className="w-full min-h-[52px] rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-4 text-base text-gray-900 dark:text-white focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary/30"
            />
            {etat.statut === "erreur" && (
                <p id="dossier-compte-erreur" role="alert" className="mt-2 mb-0 text-sm text-red-600 dark:text-red-400">
                    {etat.message}
                </p>
            )}
            <button
                type="submit"
                disabled={etat.statut === "envoi"}
                className="mt-5 w-full min-h-[52px] rounded-xl bg-[#D04515] px-5 text-base font-semibold text-white transition-colors duration-200 hover:bg-[#B83C12] disabled:opacity-60"
            >
                {etat.statut === "envoi" ? t.envoi : t.bouton}
            </button>
            <p className="mt-4 mb-0 text-center text-sm">
                <Link
                    href={{ pathname: "/login", query: { callbackUrl: getPathname({ locale: lang, href: { pathname: retour } }) } }}
                    className="inline-flex min-h-[44px] items-center text-gray-600 dark:text-lightBlue underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary"
                >
                    {t.motDePasse}
                </Link>
            </p>
        </form>
    );
}
